import { CutDirection } from "./cutUtils";
export const KERF = 4.4;

export function updateWork(work, createdTables, colors) {
  const patch = {};

  let sumCutLength;
  let sumMeasure;
  let sumParts;
  let sumUsage;
  let usageCount = 0;
  let woodPrice;

  const kantMap = {};
  const colorCount = {};

  for (const t of createdTables) {
    if (typeof t.cutLength === "number") {
      sumCutLength = (sumCutLength ?? 0) + t.cutLength;
    }

    if (typeof t.measureCount === "number") {
      sumMeasure = (sumMeasure ?? 0) + t.measureCount;
    }

    if (typeof t.partCount === "number") {
      sumParts = (sumParts ?? 0) + t.partCount;
    }

    if (typeof t.tableUsage === "number") {
      sumUsage = (sumUsage ?? 0) + t.tableUsage;
      usageCount++;
    }

    const colorId = t.color?.colorId;

    const color =
      colorId != null
        ? colors?.find((c) => String(c.colorId) === String(colorId))
        : null;

    const isMDF =
      String(color?.materialType ?? "")
        .trim()
        .toUpperCase() === "MDF";

    /*
     * Ár számítás:
     *
     * Normál anyag:
     *   price = tábla ára
     *
     * MDF:
     *   price = m2 egységár
     *   tableUsage = felhasznált m2
     *
     * ezért:
     *   MDF ár = tableUsage * price
     */

    if (typeof t.price === "number") {
      const priceContribution = isMDF
        ? Number(t.tableUsage ?? 0) * t.price
        : t.price;

      woodPrice = (woodPrice ?? 0) + priceContribution;
    }
    console.log(
      "updateWork: t.price",
      t.price,
      "isMDF",
      isMDF,
      "woodPrice",
      woodPrice
    );
    if (t.kantLength) {
      const parts = t.kantLength.split("|");

      for (const p of parts) {
        const [key, val] = p.split(":");

        if (!key || !val) continue;

        const num = Number(val);

        if (!Number.isFinite(num)) continue;

        kantMap[key] = (kantMap[key] ?? 0) + num;
      }
    }

    if (colorId != null && color) {
      /*
       * MDF-nél ez a usedTables számlálás továbbra is
       * csak reprezentációs adat.
       *
       * Az árat NEM ebből számoljuk.
       */
      let count = 1;

      if (!isMDF && color.splitDimension && t.size) {
        const size = t.size.replace(/\[|\]/g, "").split(",").map(Number);

        const split = color.splitDimension
          .replace(/\[|\]/g, "")
          .split(",")
          .map(Number);

        if (
          size.length >= 2 &&
          split.length >= 2 &&
          Math.abs(size[0] - split[0]) < 0.01 &&
          Math.abs(size[1] - split[1]) < 0.01
        ) {
          count = 0.5;
        }
      }

      colorCount[colorId] = (colorCount[colorId] ?? 0) + count;
    }
  }

  if (sumCutLength !== undefined) {
    patch.cutLength = sumCutLength;
  }

  if (sumMeasure !== undefined) {
    patch.measureCount = sumMeasure;
  }

  if (sumParts !== undefined) {
    patch.partCount = sumParts;
  }

  if (sumUsage !== undefined && usageCount > 0) {
    patch.tableUsage = sumUsage / usageCount;
  }

  if (Object.keys(kantMap).length > 0) {
    patch.kantLength = Object.entries(kantMap)
      .map(([k, v]) => `${k}:${v.toFixed(1)}`)
      .join("|");
  }

  if (Object.keys(colorCount).length > 0) {
    patch.usedTables =
      "[" +
      Object.entries(colorCount)
        .map(([k, v]) => `${k}:${v.toFixed(1)}`)
        .join("|") +
      "]";
  }

  if (woodPrice !== undefined) {
    patch.woodPrice = woodPrice;

    if (typeof work.label === "number") {
      patch.clientPrice = woodPrice + work.label;
    }
  }

  return {
    ...work,
    ...patch
  };
}

export function parseSplitDimension(color) {
  if (!color?.splitDimension) return null;
  const s = color.splitDimension.replace("[", "").replace("]", "");
  const p = s.split(",").map((n) => Number(n.trim()));
  if (p.length < 2 || p[0] <= 0 || p[1] <= 0) return null;
  return { h: p[0], w: p[1] };
}

export function tableUtilization(table, placedRects) {
  const rects = placedRects.filter((r) => r.id === table.id);
  const used = rects.reduce((s, r) => s + r.width * r.height, 0);
  const dims = table.size
    .replace("[", "")
    .replace("]", "")
    .split(",")
    .map(Number);
  const area = dims[0] * dims[1];
  return area > 0 ? used / area : 0;
}

export function normalizeDirection(direction) {
  const value = String(direction ?? "both")
    .trim()
    .toLowerCase();

  if (value === "horizontal" || value === "horizontális") {
    return CutDirection.HORIZONTAL;
  }

  if (
    value === "vertical" ||
    value === "vertikális" ||
    value === "függőleges"
  ) {
    return CutDirection.VERTICAL;
  }

  return "both";
}

export function sameColorAndMilling(table, colorId, millingId) {
  return (
    String(table.color?.colorId) === String(colorId) &&
    String(table.millingId ?? table.milling?.millingId ?? "no-milling") ===
      String(millingId ?? "no-milling")
  );
}

export function rectKey(rect) {
  return rect.mainid ?? rect.localid;
}

export function restoreOriginalRectForRetry(r) {
  const placedW = r.rotated ? r.height : r.width;
  const placedH = r.rotated ? r.width : r.height;

  return {
    ...r,
    width: placedW - KERF,
    height: placedH - KERF,
    rotated: false,
    xpos: 0,
    ypos: 0,
    rotable: r.rotable ?? r.createdItem?.rotable ?? true
  };
}

export function getResultArea(result) {
  return result.placedRects.reduce((s, r) => s + r.width * r.height, 0);
}

export function getPlanScore(plan, colors, work) {
  const updatedWork = updateWork(work, plan.createdTables, colors);

  const usedTables =
    Number(
      String(updatedWork.usedTables ?? "")
        .replace("[", "")
        .replace("]", "")
        .split("|")
        .map((p) => Number(p.split(":")[1]))
        .filter(Number.isFinite)
        .reduce((s, n) => s + n, 0)
        .toFixed(3)
    ) || plan.createdTables.length;

  const woodPrice =
    typeof updatedWork.woodPrice === "number"
      ? updatedWork.woodPrice
      : Infinity;

  const avgUsage =
    plan.createdTables.length > 0
      ? plan.createdTables.reduce((s, t) => s + (t.tableUsage ?? 0), 0) /
        plan.createdTables.length
      : 0;

  return {
    unplacedCount: plan.remainingRects.length,
    usedTables,
    woodPrice,
    tableCount: plan.createdTables.length,
    negativeUsage: -avgUsage
  };
}

export function isBetterPlan(candidate, currentBest, colors, work) {
  if (!currentBest) return true;

  const a = getPlanScore(candidate, colors, work);
  const b = getPlanScore(currentBest, colors, work);

  const keys = [
    "unplacedCount",
    "usedTables",
    "woodPrice",
    "tableCount",
    "negativeUsage"
  ];

  for (const key of keys) {
    if (a[key] < b[key]) return true;
    if (a[key] > b[key]) return false;
  }

  return false;
}

export function logOptimizePlanResult(plan, colors, work, createdItems = []) {
  const score = getPlanScore(plan, colors, work);

  console.group(`📊 Optimize result: ${plan.subMode}`);

  console.log("placed:", plan.placedRects.length);
  console.log("unplaced:", plan.remainingRects.length);
  console.log("table records:", plan.createdTables.length);
  console.log("used tables:", score.usedTables);
  console.log("wood price:", score.woodPrice);
  console.log("avg usage:", Number((-score.negativeUsage).toFixed(4)));

  console.table(
    plan.createdTables.map((t) => ({
      id: t.id,
      tableIndex: t.tableIndex,
      size: t.size,
      usage: Number((t.tableUsage ?? 0).toFixed(4)),
      parts: t.partCount,
      price: t.price
    }))
  );

  for (const table of plan.createdTables) {
    const rectsOnTable = plan.placedRects.filter((r) => r.id === table.id);

    console.groupCollapsed(
      `▸ ${plan.subMode} | table id=${table.id} | size=${table.size} | parts=${rectsOnTable.length}`
    );

    console.table(
      rectsOnTable.map((r) => {
        const itemId = r.mainid ?? r.localid ?? r.createdItem?.itemId;

        const item = createdItems?.find(
          (ci) => String(ci.itemId) === String(itemId)
        );

        return {
          itemId,
          name:
            item?.name ??
            item?.label ??
            item?.description ??
            r.name ??
            r.label ??
            "",
          width: Number(((r.width ?? 0) - KERF).toFixed(1)),
          height: Number(((r.height ?? 0) - KERF).toFixed(1)),
          placedWidth: Number((r.width ?? 0).toFixed(1)),
          placedHeight: Number((r.height ?? 0).toFixed(1)),
          x: Number((r.xpos ?? 0).toFixed(1)),
          y: Number((r.ypos ?? 0).toFixed(1)),
          rotated: r.rotated === true,
          rootMainDir: r.rootMainDir
        };
      })
    );

    console.groupEnd();
  }

  console.groupEnd();
}
